import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  IconButton,
  Box,
  Alert,
  Slider,
  Autocomplete,
  CircularProgress
} from '@mui/material';
import { 
  Users,
  X,
  Target,
  Sparkles,
  TrendingUp
} from 'lucide-react';
import Button from '../common/Button';

const AutoShortlistModal = ({
  open,
  onClose,
  onSubmit,
  jobs = [],
  isLoading = false,
  isSearching = false,
  onSearchChange,
  title = "Auto Shortlist Candidates",
  submitButtonText = "Run Auto Shortlist"
}) => {
  const [formData, setFormData] = useState({
    job_id: '',
    min_score: 0.0,
    max_candidates: 5
  });

  const [errors, setErrors] = useState({});

   const [selectedJob, setSelectedJob] = useState(null);

  // Find selected job when formData.job_id changes
  // useEffect(() => {
  //   if (formData.job_id && jobs.length > 0) {
  //     const foundJob = jobs.find(job => String(job.id) === String(formData.job_id));
  //     setSelectedJob(foundJob || null);
  //   } else {
  //     setSelectedJob(null);
  //   }
  // }, [formData.job_id, jobs]);


  useEffect(() => {
    if (open) {
      // Reset form when modal opens
      setFormData({
        job_id: '',
        min_score: 0.0,
        max_candidates: 5
      });
      setSelectedJob(null);
      setErrors({});
    }
  }, [open]);


  const handleChange = (field) => (event) => {
    const value = event.target.value;
    
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleJobChange = (event, newValue) => {
    // newValue is the selected job object or null
    setSelectedJob(newValue);
    setFormData(prev => ({
      ...prev,
      job_id: newValue ? String(newValue.id) : ''
    }));
    
    // Clear error if exists
    if (errors.job_id) {
      setErrors(prev => ({
        ...prev,
        job_id: ''
      }));
    }
  };

  const handleSearchChange = (event, value) => {
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleSliderChange = (field) => (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      [field]: newValue
    }));
  };


  const validateForm = () => {
    const newErrors = {};

    if (!formData.job_id) {
      newErrors.job_id = 'Please select a job';
    }

    if (formData.min_score < 0 || formData.min_score > 100) {
      newErrors.min_score = 'Score must be between 0.0 and 100';
    }

    if (formData.max_candidates < 1 || formData.max_candidates > 50) {
      newErrors.max_candidates = 'Number of candidates must be between 1 and 50';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      const submitData = {
        ...formData,
        min_score: parseFloat(formData.min_score),
        max_candidates: parseInt(formData.max_candidates)
      };
      
      onSubmit(submitData);
    }
  };

  const handleClose = () => {
    setErrors({});
    onClose();
  };

  const marks = [
    { value: 1, label: '1' },
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 30, label: '30' },
    { value: 40, label: '40' },
    { value: 50, label: '50' }
  ];

  console.log("Selected JOb", selectedJob)

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <form onSubmit={handleSubmit} className='sentry-mask'>
        <DialogTitle className="border-b">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <Typography variant="h6" className="text-secondary-900">
                {title}
              </Typography>
            </div>
            <IconButton
              aria-label="close"
              onClick={handleClose}
              sx={{ color: 'gray' }}
            >
              <X />
            </IconButton>
          </div>
        </DialogTitle>

        <DialogContent className="pt-6 mt-4">
          <Grid container spacing={4}>
            <Grid size={{xs: 12}}>
              <Alert severity="info" className="mb-4">
                <div className="flex items-start space-x-2">
                  <Target className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <Typography variant="body2" className="font-medium">
                      AI-Powered Candidate Matching
                    </Typography>
                    <Typography variant="body2" className="text-gray-600">
                      Our algorithm will automatically find the best candidates based on skills, experience, and job requirements.
                    </Typography>
                  </div>
                </div>
              </Alert>
            </Grid>

            {/* Job Selection */}
            <Grid size={{xs: 12}}>
              <Autocomplete
                options={jobs}
                value={selectedJob}
                onChange={handleJobChange}
                onInputChange={handleSearchChange}
                getOptionLabel={(option) => {
                  if (!option) return '';
                  return `${option.title || 'Untitled'} - ${option.num_applicants || 0} applications`;
                }}
                isOptionEqualToValue={(option, value) => 
                  option && value ? String(option.id) === String(value.id) : option === value
                }
                loading={isSearching}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Select Job"
                    required
                    error={!!errors.job_id}
                    helperText={errors.job_id || "Start typing to search for jobs"}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: (
                          <>
                            <Target className="w-4 h-4 text-gray-400 mr-2" />
                            {params.InputProps.startAdornment}
                          </>
                        ),
                        endAdornment: (
                          <>
                            {isSearching ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        ),
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props} key={option.id}>
                    <div className="flex flex-col w-full">
                      <span className="font-medium">{option.title || 'Untitled'}</span>
                      <div className="flex justify-between text-xs text-gray-500 mt-1">
                        <span>{option.num_applicants || 0} applications</span>
                        {option.status && (
                          <span className={`px-2 py-0.5 rounded-full ${
                            option.status === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : option.status === 'draft'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {option.status}
                          </span>
                        )}
                      </div>
                    </div>
                  </li>
                )}
                noOptionsText={
                  jobs.length === 0 && !isSearching
                    ? "No jobs found. Try a different search."
                    : "Start typing to search for jobs"
                }
                filterOptions={(x) => x} // Disable client-side filtering since parent handles it
              />
            </Grid>
            {/* <Grid size={{xs: 12}}>
              <FormControl fullWidth required error={!!errors.job_id}>
                <InputLabel>Select Job</InputLabel>
                <Select
                  value={formData.job_id}
                  label="Select Job"
                  onChange={handleChange('job_id')}
                  startAdornment={<Target className="w-4 h-4 text-gray-400 mr-2" />}
                >
                  {jobs.map((job) => (
                    <MenuItem key={job.id} value={job.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{job.title}</span>
                        <span className="text-xs text-gray-500">
                          {job.num_applicants || 0} applications
                        </span>
                      </div>
                    </MenuItem>
                  ))}
                </Select>
                {errors.job_id && (
                  <Typography variant="caption" color="error">
                    {errors.job_id}
                  </Typography>
                )}
              </FormControl>
            </Grid> */}

            {/* Matching Score Threshold */}
            <Grid size={{xs: 12, md: 6}}>
              <Typography variant="subtitle2" className="font-semibold mb-2 text-secondary-900">
                Minimum Match Score
              </Typography>
              <TextField
                fullWidth
                type="number"
                value={formData.min_score}
                onChange={handleChange('min_score')}
                error={!!errors.min_score}
                helperText={errors.min_score || "Minimum AI match score (0.0 - 100)"}
                
                slotProps={{
                  input: {
                    endAdornment: (
                      <TrendingUp className="w-4 h-4 text-gray-400" />
                    ),       
                  },
                  htmlInput: {
                    min: 0.0, 
                  max: 100, 
                  step: 0.1
                  }
                }}
              />
              <div className="mt-2">
                <Typography variant="caption" className="text-gray-600">
                  Higher scores = better matches (70+ recommended)
                </Typography>
              </div>
            </Grid>

            {/* Maximum Candidates */}
            <Grid size={{xs: 12, md: 6}}>
              <Typography variant="subtitle2" className="font-semibold mb-2 text-secondary-900">
                Maximum Candidates: {formData.max_candidates}
              </Typography>
              <Box className="px-2">
                <Slider
                  value={formData.max_candidates}
                  onChange={handleSliderChange('max_candidates')}
                  aria-labelledby="max-candidates-slider"
                  valueLabelDisplay="auto"
                  step={1}
                  marks={marks}
                  min={1}
                  max={50}
                  color="primary"
                />
              </Box>
              <Typography variant="caption" className="text-gray-600 block mt-1">
                Limit the number of candidates to shortlist
              </Typography>
              {errors.max_candidates && (
                <Typography variant="caption" color="error">
                  {errors.max_candidates}
                </Typography>
              )}
            </Grid>

            {/* Summary Card */}
            <Grid size={{xs: 12}}>
              <div className="bg-gray-50 rounded-lg p-4 border">
                <Typography variant="subtitle2" className="font-semibold mb-2 text-secondary-900">
                  Shortlist Summary
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="flex items-center space-x-2">
                    <Users className="w-4 h-4 text-gray-500" />
                    <span>
                      <strong>Job:</strong> {
                        jobs.find(job => job.id === parseInt(formData.job_id))?.title ||selectedJob?.title || 'Not selected'
                      }
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Target className="w-4 h-4 text-gray-500" />
                    <span>
                      <strong>Min Score:</strong> {formData.min_score}
                    </span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Sparkles className="w-4 h-4 text-gray-500" />
                    <span>
                      <strong>Max Candidates:</strong> {formData.max_candidates}
                    </span>
                  </div>
                </div>
              </div>
            </Grid>

            {/* How it works */}
            <Grid size={{xs: 12}}>
              <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
                <Typography variant="subtitle2" className="font-semibold mb-2 text-blue-900">
                  How Auto Shortlisting Works
                </Typography>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li>• Analyzes candidate skills against job requirements</li>
                  <li>• Considers experience level and qualifications</li>
                  <li>• Uses AI matching algorithm for optimal results</li>
                  <li>• Automatically updates candidate status to "shortlisted"</li>
                </ul>
              </div>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions className="border-t p-4">
          <div className="flex space-x-3 w-full justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isLoading || isSearching}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isLoading}
              disabled={!selectedJob || isSearching}
              icon={<Sparkles className="w-4 h-4" />}
            >
              {submitButtonText}
            </Button>
          </div>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default AutoShortlistModal;